import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, CalendarDays, Play, ArrowRight, Search, LayoutGrid, List, Edit, Trash2, MoreHorizontal, ArrowUp, Share2, CheckCircle, Clock } from 'lucide-react';
import useStore from '@/store/useStore';
import { Match } from '@/types/cricket';
import { format } from 'date-fns';
import { EntityActions } from '@/components/crud/EntityActions';
import { toast } from '@/components/ui/use-toast';
import UniqueIdDisplay from '@/components/common/UniqueIdDisplay';
import MatchHoverCard from '@/components/match/MatchHoverCard';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { MoreVertical } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const MatchesPage = () => {
  const navigate = useNavigate();
  const { 
    matches, 
    teams, 
    tournaments,
    players,
    fetchMatches, 
    fetchTeams, 
    fetchTournaments,
    fetchPlayers,
    updateMatch,
    deleteMatch,
    fetchUsers
  } = useStore();
  const [activeTab, setActiveTab] = useState("upcoming");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  
  // State for search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTotalOvers, setFilterTotalOvers] = useState<string>('all');
  const [filterBallsPerOver, setFilterBallsPerOver] = useState<string>('all');
  const [filterWicketsPerInnings, setFilterWicketsPerInnings] = useState<string>('all');
  const [filterMatchType, setFilterMatchType] = useState<string>('all');
  const [filterDate, setFilterDate] = useState<Date | undefined>(undefined);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editingMatch, setEditingMatch] = useState<Match | null>(null);
  const [sortKey, setSortKey] = useState<string>('matchDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          fetchMatches(),
          fetchTeams(),
          fetchTournaments(),
          fetchPlayers(),
          fetchUsers && fetchUsers()
        ]);
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };
    
    loadData();
  }, [fetchMatches, fetchTeams, fetchTournaments, fetchPlayers, fetchUsers]);
  
  // Derive unique filter options from matches
  const uniqueTotalOvers = Array.from(new Set(matches.map(m => m.totalOvers).filter(o => o !== undefined && o !== null)))
    .sort((a, b) => a! - b!); // Numeric sort
  console.log("Unique Total Overs:", uniqueTotalOvers);

  const uniqueBallsPerOver = Array.from(new Set(matches.map(m => m.ballsPerOver).filter(b => b !== undefined && b !== null)))
    .sort((a, b) => a! - b!); // Numeric sort
  console.log("Unique Balls Per Over:", uniqueBallsPerOver);

  const uniqueWicketsPerInnings = Array.from(new Set(matches.map(m => m.wicketsPerInnings).filter(w => w !== undefined && w !== null)))
    .sort((a, b) => a! - b!); // Numeric sort
  console.log("Unique Wickets Per Innings:", uniqueWicketsPerInnings);

  const uniqueMatchTypes = Array.from(new Set(matches.map(m => m.matchType).filter(mt => mt !== undefined && mt !== null && mt !== '')))
    .sort((a, b) => a!.localeCompare(b!)); // Alphabetical sort
  console.log("Unique Match Types:", uniqueMatchTypes);

  // Filtering logic
  const getFilteredMatches = (matchesToFilter: Match[]) => {
    return matchesToFilter.filter(match => {
      // Text Search
      const team1Name = teams.find(t => t.id === match.team1Id)?.name || '';
      const team2Name = teams.find(t => t.id === match.team2Id)?.name || '';
      const tournamentName = tournaments.find(t => t.id === match.tournamentId)?.name || '';
      const city = match.city || '';

      const searchLower = searchTerm.toLowerCase();
      const textMatches =
        team1Name.toLowerCase().includes(searchLower) ||
        team2Name.toLowerCase().includes(searchLower) ||
        tournamentName.toLowerCase().includes(searchLower) ||
        city.toLowerCase().includes(searchLower);

      if (searchTerm && !textMatches) return false;

      // Numerical Filters
      if (filterTotalOvers !== 'all' && match.totalOvers !== parseInt(filterTotalOvers)) return false;
      if (filterBallsPerOver !== 'all' && match.ballsPerOver !== parseInt(filterBallsPerOver)) return false;
      if (filterWicketsPerInnings !== 'all' && match.wicketsPerInnings !== parseInt(filterWicketsPerInnings)) return false;

      // Match Type Filter
      if (filterMatchType !== 'all' && match.matchType !== filterMatchType) return false;

      // Date Filter
      if (filterDate) {
        const matchDate = new Date(match.matchDate);
        // Compare year, month, and day only
        if (
          matchDate.getFullYear() !== filterDate.getFullYear() ||
          matchDate.getMonth() !== filterDate.getMonth() ||
          matchDate.getDate() !== filterDate.getDate()
        ) {
          return false;
        }
      }

      return true;
    });
  };
  
  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  // Sorting logic for matches
  const applySort = (matchesToSort: Match[]) => {
    if (!sortKey) return matchesToSort;
    return [...matchesToSort].sort((a, b) => {
      let aValue: any = a[sortKey as keyof Match];
      let bValue: any = b[sortKey as keyof Match];
      if (sortKey === 'createdAt' || sortKey === 'updatedAt' || sortKey === 'matchDate') {
        aValue = aValue ? new Date(aValue) : new Date(0);
        bValue = bValue ? new Date(bValue) : new Date(0);
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  };

  const upcomingMatches = applySort(getFilteredMatches(matches.filter(match => match.status === 'upcoming')));
  const liveMatches = applySort(getFilteredMatches(matches.filter(match => match.status === 'live')));
  const completedMatches = applySort(getFilteredMatches(matches.filter(match => match.status === 'completed')));
  
  const hasTeams = teams.length >= 2;
  const hasTournaments = tournaments.length > 0;
  
  const renderMatchCard = (match: Match) => {
    const team1 = teams.find(t => t.id === match.team1Id);
    const team2 = teams.find(t => t.id === match.team2Id);
    const tournament = tournaments.find(t => t.id === match.tournamentId);
    
    const isLive = match.status === 'live';
    const isUpcoming = match.status === 'upcoming';
    
    const getFormattedDate = (dateString: string) => {
      try {
        return format(new Date(dateString), 'PPP');
      } catch (e) {
        return dateString;
      }
    };
    
    const team1Players = team1?.players || [];
    const team2Players = team2?.players || [];
    const hasEnoughPlayers = team1Players.length >= 2 && team2Players.length >= 1;
    
    const handleGoLive = (e: React.MouseEvent) => {
      e.preventDefault();
      if (!hasEnoughPlayers) {
        toast({
          title: "Cannot start match",
          description: "Teams need more players. Batting team needs at least 2 players.",
          variant: "destructive",
        });
      } else {
        navigate(`/matches/${match.id}`);
      }
    };
    
    return (
      <MatchHoverCard
        key={match.id}
        match={match}
        team1={team1}
        team2={team2}
        tournament={tournament}
      >
        <Card className="hover:shadow-lg transition-shadow cursor-pointer">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <CardTitle className="flex items-center gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="block max-w-xs truncate" title={`${match.team1Name} vs ${match.team2Name}`}>
                        {match.team1Name} vs {match.team2Name}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {match.team1Name} vs {match.team2Name}
                    </TooltipContent>
                  </Tooltip>
                  {match.status === 'upcoming' && (
                    <Clock className="h-5 w-5 text-yellow-500" title="Upcoming Match" />
                  )}
                  {isLive && (
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                    </span>
                  )}
                  {match.status === 'completed' && (
                    <CheckCircle className="h-5 w-5 text-green-500" title="Match Completed" />
                  )}
                </CardTitle>
                <CardDescription>
                  {tournament?.name} • {match.city}
                </CardDescription>
                {match.uniqueId && (
                  <div className="mt-2">
                    <UniqueIdDisplay uniqueId={match.uniqueId} />
                  </div>
                )}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="p-2 rounded-full hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary">
                    <MoreVertical className="w-5 h-5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => {
                      const url = `${window.location.origin}/matches/live/${match.id}`;
                      navigator.clipboard.writeText(url);
                      toast({
                        title: 'Link copied!',
                        description: 'Live score link copied to clipboard.',
                      });
                    }}
                  >
                    <Share2 className="w-4 h-4 mr-2" /> Share
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleEditMatch(match)}>
                    <Edit className="w-4 h-4 mr-2" /> Change
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => deleteMatch(match.id)} className="text-red-600 focus:text-red-700">
                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </CardHeader>
          <CardContent className="space-y-2 bg-card">
            <div className="text-sm">
              <span className="font-medium">Date:</span> {getFormattedDate(match.matchDate)}
            </div>
            
            {!isUpcoming && (
              <div className="grid grid-cols-2 gap-4 mt-3 p-3 bg-muted rounded-lg dark:bg-[var(--background-dark)]">
                <div className="text-center">
                  <p className="text-xs font-medium">{match.team1Name}</p>
                  <p className="text-lg font-bold">{match.team1Score.runs}/{match.team1Score.wickets}</p>
                  <p className="text-xs">
                    {match.team1Score.overs}.{match.team1Score.balls} overs
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs font-medium">{match.team2Name}</p>
                  <p className="text-lg font-bold">{match.team2Score.runs}/{match.team2Score.wickets}</p>
                  <p className="text-xs">
                    {match.team2Score.overs}.{match.team2Score.balls} overs
                  </p>
                </div>
              </div>
            )}
            {isUpcoming && (
                <div className="h-20 bg-muted rounded-lg mt-3 flex items-center justify-center text-muted-foreground text-sm border border-dashed border-border dark:bg-[var(--background-dark)]">
                    Match details will appear here when live.
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/matches/${match.id}?tab=match-info`}>
                View Details
              </Link>
            </Button>
            
            {isUpcoming && (
              <Button 
                size="sm" 
                onClick={handleGoLive}
                className="flex items-center gap-1"
                disabled={!hasEnoughPlayers}
              >
                <Play className="h-4 w-4" /> Go Live
              </Button>
            )}
            {!isUpcoming && match.status !== 'completed' && (
              <Button 
                size="sm" 
                onClick={() => navigate(`/matches/${match.id}`)}
                className="flex items-center gap-1"
              >
                <ArrowRight className="h-4 w-4" /> Continue Match
              </Button>
            )}
            {match.status === 'completed' && (
              <Button
                size="sm"
                onClick={() => navigate(`/matches/${match.id}`)}
                className="flex items-center gap-1"
              >
                <ArrowRight className="h-4 w-4" /> View Match Summary
              </Button>
            )}
          </CardFooter>
        </Card>
      </MatchHoverCard>
    );
  };
  
  const renderMatchListItem = (match: Match) => {
    const team1 = teams.find(t => t.id === match.team1Id);
    const team2 = teams.find(t => t.id === match.team2Id);
    const tournament = tournaments.find(t => t.id === match.tournamentId);
    
    const isLive = match.status === 'live';
    const isUpcoming = match.status === 'upcoming';

    const getFormattedDate = (dateString: string) => {
      try {
        return format(new Date(dateString), 'PPP');
      } catch (e) {
        return dateString;
      }
    };

    const team1Players = team1?.players || [];
    const team2Players = team2?.players || [];
    const hasEnoughPlayers = team1Players.length >= 2 && team2Players.length >= 1;

    const handleGoLive = (e: React.MouseEvent) => {
                e.preventDefault();
      if (!hasEnoughPlayers) {
                toast({
          title: "Cannot start match",
          description: "Teams need more players. Batting team needs at least 2 players.",
                  variant: "destructive",
                });
      } else {
        navigate(`/matches/${match.id}`);
      }
    };

    return (
      <MatchHoverCard
        key={match.id}
        match={match}
        team1={team1}
        team2={team2}
        tournament={tournament}
      >
        <div className="flex items-start justify-between p-4 border-b hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer last:border-b-0">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-semibold truncate">
                {match.team1Name} vs {match.team2Name}
              </h3>
              {isLive && (
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 truncate">
              {tournament?.name} • {match.city} • {getFormattedDate(match.matchDate)}
            </p>
            {match.uniqueId && (
              <div className="mt-1">
                <UniqueIdDisplay uniqueId={match.uniqueId} />
              </div>
            )}
          </div>
          {!isUpcoming && (
            <div className="flex items-center gap-4 ml-4">
              <div className="text-right">
                <p className="text-xs font-medium">{match.team1Name}</p>
                <p className="text-base font-bold">{match.team1Score.runs}/{match.team1Score.wickets}</p>
                <p className="text-xs">
                  {match.team1Score.overs}.{match.team1Score.balls} overs
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs font-medium">{match.team2Name}</p>
                <p className="text-base font-bold">{match.team2Score.runs}/{match.team2Score.wickets}</p>
                <p className="text-xs">
                  {match.team2Score.overs}.{match.team2Score.balls} overs
                </p>
              </div>
            </div>
          )}
          <div className="ml-4 flex-shrink-0 flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <Link to={`/matches/${match.id}?tab=match-info`}>
                View Details
              </Link>
            </Button>
            {isUpcoming && (
              <Button 
                size="sm" 
                onClick={handleGoLive}
                className="flex items-center gap-1"
                disabled={!hasEnoughPlayers}
              >
                <Play className="h-4 w-4" /> Go Live
              </Button>
            )}
            {!isUpcoming && match.status !== 'completed' && (
              <Button 
                size="sm" 
                onClick={() => navigate(`/matches/${match.id}`)}
                className="flex items-center gap-1"
          >
                <ArrowRight className="h-4 w-4" /> Continue Match
              </Button>
            )}
            {match.status === 'completed' && (
              <Button
                size="sm"
                onClick={() => navigate(`/matches/${match.id}`)}
                className="flex items-center gap-1"
              >
                <ArrowRight className="h-4 w-4" /> View Match Summary
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 w-10 p-0">
                  <MoreHorizontal className="h-4 w-4 text-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={() => {
                    const url = `${window.location.origin}/matches/live/${match.id}`;
                    navigator.clipboard.writeText(url);
                    toast({
                      title: 'Link copied!',
                      description: 'Live score link copied to clipboard.',
                    });
                  }}
                >
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleEditMatch(match)}>
                  <Edit className="w-4 h-4 mr-2" /> Change
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => deleteMatch(match.id)} className="text-red-600 focus:text-red-700">
                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </MatchHoverCard>
    );
  };
  
  // Handler for opening edit dialog
  const handleEditMatch = (match: Match) => {
    setEditingMatch(match);
    setIsEditOpen(true);
  };
  // Handler for saving match
  const handleSaveEdit = async (updatedMatch: Match) => {
    await updateMatch(updatedMatch);
    setIsEditOpen(false);
    setEditingMatch(null);
  };
  // Handler for cancel
  const handleCancelEdit = () => {
    setIsEditOpen(false);
    setEditingMatch(null);
  };
  
  return (
    <div className="container mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">Matches</h1>
          <p className="text-muted-foreground">Schedule and manage cricket matches</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button asChild>
            <Link to="/matches/new" className="flex items-center gap-1">
              <Plus className="h-4 w-4" /> Create New Match
            </Link>
          </Button>
        </div>
      </div>
      
      <div className="mb-6 flex items-center justify-between gap-2 overflow-x-auto pb-2 no-scrollbar">
        <div className="flex items-center gap-2">
          <div className="relative flex-none w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500" />
          <Input
              type="text" 
              placeholder="Search matches..." 
              className="pl-9 pr-3 py-2 w-full"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

          <Select onValueChange={setFilterTotalOvers} value={filterTotalOvers}>
            <SelectTrigger className="w-[160px] flex-none">
              <SelectValue placeholder="Total Overs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Total Overs</SelectItem>
              {uniqueTotalOvers.map(overs => (
                <SelectItem key={overs} value={String(overs)}>{overs} Overs</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select onValueChange={setFilterBallsPerOver} value={filterBallsPerOver}>
            <SelectTrigger className="w-[160px] flex-none">
              <SelectValue placeholder="Balls Per Over" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Balls Per Over</SelectItem>
              {uniqueBallsPerOver.map(balls => (
                <SelectItem key={balls} value={String(balls)}>{balls} Balls</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select onValueChange={setFilterWicketsPerInnings} value={filterWicketsPerInnings}>
            <SelectTrigger className="w-[180px] flex-none">
              <SelectValue placeholder="Wickets Per Innings" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Wickets Per Innings</SelectItem>
              {uniqueWicketsPerInnings.map(wickets => (
                <SelectItem key={wickets} value={String(wickets)}>{wickets} Wickets</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select onValueChange={setFilterMatchType} value={filterMatchType}>
            <SelectTrigger className="w-[160px] flex-none">
              <SelectValue placeholder="Match Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Match Types</SelectItem>
              {uniqueMatchTypes.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"outline"}
                className={cn(
                  "w-[180px] justify-start text-left font-normal flex-none",
                  !filterDate && "text-muted-foreground"
                )}
              >
                <CalendarDays className="mr-2 h-4 w-4" />
                {filterDate ? format(filterDate, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={filterDate}
                onSelect={setFilterDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          {filterDate && (
            <Button variant="outline" onClick={() => setFilterDate(undefined)} className="flex-none">
              Clear Date
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2 flex-none">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="border border-input bg-background hover:bg-accent hover:text-accent-foreground h-10 w-10 p-0">
                <MoreHorizontal className="h-4 w-4 text-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {[
                { key: 'matchDate', label: 'Date' },
                { key: 'name', label: 'Name' },
                { key: 'createdAt', label: 'Created Date' },
                { key: 'updatedAt', label: 'Modified Date' },
              ].map(option => (
                <DropdownMenuItem key={option.key} onSelect={() => handleSort(option.key)}>
                  <span>{option.label}</span>
                  {sortKey === option.key && <ArrowUp className={`ml-auto h-4 w-4 ${sortDirection === 'desc' ? 'transform rotate-180' : ''}`} />}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}>
                Toggle Sort Direction
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        <div className="flex rounded-md shadow-sm border flex-none">
            <Button 
              variant={viewMode === "grid" ? "default" : "ghost"}
              onClick={() => setViewMode("grid")}
              className="rounded-r-none"
            >
              <LayoutGrid className="h-5 w-5" />
            </Button>
            <Button 
              variant={viewMode === "list" ? "default" : "ghost"}
              onClick={() => setViewMode("list")}
              className="rounded-l-none border-l"
            >
              <List className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>
      
      {!hasTeams && (
        <p className="text-orange-500 mb-4">
          Please create at least two teams to create a match.
        </p>
      )}
      {!hasTournaments && (
        <p className="text-orange-500 mb-4">
          Please create at least one tournament to create a match.
        </p>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="upcoming">Upcoming ({upcomingMatches.length})</TabsTrigger>
          <TabsTrigger value="live">Live ({liveMatches.length})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({completedMatches.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="upcoming" className="mt-4">
          {upcomingMatches.length === 0 ? (
            <p className="text-gray-500">No upcoming matches.</p>
          ) : (
            viewMode === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {upcomingMatches.map(renderMatchCard)}
            </div>
          ) : (
              <div className="border rounded-md divide-y">
                {upcomingMatches.map(renderMatchListItem)}
            </div>
            )
          )}
        </TabsContent>
        <TabsContent value="live" className="mt-4">
          {liveMatches.length === 0 ? (
            <p className="text-gray-500">No live matches.</p>
          ) : (
            viewMode === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {liveMatches.map(renderMatchCard)}
            </div>
          ) : (
              <div className="border rounded-md divide-y">
                {liveMatches.map(renderMatchListItem)}
            </div>
            )
          )}
        </TabsContent>
        <TabsContent value="completed" className="mt-4">
          {completedMatches.length === 0 ? (
            <p className="text-gray-500">No completed matches.</p>
          ) : (
            viewMode === "grid" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {completedMatches.map(renderMatchCard)}
            </div>
          ) : (
              <div className="border rounded-md divide-y">
                {completedMatches.map(renderMatchListItem)}
            </div>
            )
          )}
        </TabsContent>
      </Tabs>

      {/* Match Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Match</DialogTitle>
          </DialogHeader>
          {editingMatch && (
            <EntityActions
              entity={editingMatch}
              entityType="match"
              onEdit={handleSaveEdit}
              onDelete={() => {}}
              teams={teams}
              tournaments={tournaments}
              players={players}
              showDialog={false}
            />
          )}
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={handleCancelEdit}>Cancel</Button>
            <Button onClick={() => document.querySelector('form')?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }))}>Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MatchesPage;
